import { Link } from "@tanstack/react-router";
import { ArrowRight, Bot, GitBranch, ShieldCheck, Zap } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PublicNavbar } from "@/components/public-navbar";
import { SeoHead } from "@/components/seo-head";
import { JsonLd } from "@/components/json-ld";

const SOFTWARE_APPLICATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "TodoLLM",
  url: "https://todollm.com",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Task engine for hybrid human-AI teams. Structured projects, MCP server for LLM agents, append-only timelines, and scoped API keys.",
  offers: [
    { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
    {
      "@type": "Offer",
      name: "Pro",
      price: "2",
      priceCurrency: "USD",
      billingIncrement: "P1M",
    },
    {
      "@type": "Offer",
      name: "Team",
      price: "7",
      priceCurrency: "USD",
      billingIncrement: "P1M",
    },
  ],
  provider: {
    "@type": "Organization",
    name: "TodoLLM",
    url: "https://todollm.com",
    contactPoint: {
      "@type": "ContactPoint",
      email: "support@todollm.com",
      contactType: "customer support",
    },
  },
};

export function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        description="TodoLLM is the task engine for hybrid human-AI teams. Structured projects, an MCP server for LLM agents, append-only timelines, and scoped API keys. Start free."
        canonical="https://todollm.com/"
      />
      <JsonLd data={SOFTWARE_APPLICATION_SCHEMA} />
      <PublicNavbar />
      <PublicLanding />
    </div>
  );
}

function PublicLanding() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-muted/40 to-background">
        <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="outline" className="mb-6 gap-1.5 px-3 py-1 text-xs">
              <Zap className="size-3" />
              AI-native task management
            </Badge>
            <h1 className="text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
              Ship faster with{" "}
              <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                agents in the loop
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
              TodoLLM is the task engine for hybrid teams. Humans plan, agents
              execute - both working from the same structured project state over
              a simple MCP API.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg">
                <Link to="/sign-up">
                  Get started free
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/docs">Read the docs</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* decorative grid */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
        />
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="mb-12 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">
            Everything you need
          </p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
            Built for humans and agents alike
          </h2>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <FeatureCard
            icon={<GitBranch className="size-5" />}
            title="Structured projects"
            description="Orgs, projects, and tasks with dependency tracking. Every piece of work has a place - and a status."
          />
          <FeatureCard
            icon={<Bot className="size-5" />}
            title="Agent-ready MCP server"
            description="Point any MCP client at /mcp with an API key. Agents list, claim, and close tasks without custom glue."
          />
          <FeatureCard
            icon={<ShieldCheck className="size-5" />}
            title="Append-only timeline"
            description="Every status change is immutable and attributed. Full audit trail - no silent overwrites."
          />
          <FeatureCard
            icon={<Zap className="size-5" />}
            title="Scoped API keys"
            description="Mint read, write, or operate keys per org or project. Give agents exactly the permissions they need."
          />
          <FeatureCard
            icon={<ArrowRight className="size-5" />}
            title="Dependency blocking"
            description="Mark tasks as blocked until their dependencies complete. Agents only see work they can actually start."
          />
          <FeatureCard
            icon={<GitBranch className="size-5" />}
            title="Commit linking"
            description="Agents attach commit hashes when closing tasks. Reviewers trace any change back to its work item."
          />
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-border bg-muted/20">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="mb-12 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">
              Agentic loop
            </p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
              How an agent works a task
            </h2>
          </div>

          <div className="grid gap-8 sm:grid-cols-5">
            {[
              {
                step: "01",
                title: "Query",
                body: "list_tasks(status=PENDING) to find available work.",
              },
              {
                step: "02",
                title: "Claim",
                body: "claim_task() flips status to IN_PROGRESS and records the agent.",
              },
              {
                step: "03",
                title: "Context",
                body: "get_history(tail=1) to see prior attempts or human notes.",
              },
              {
                step: "04",
                title: "Execute",
                body: "The agent does the actual work: edits, tests, builds.",
              },
              {
                step: "05",
                title: "Close",
                body: "update_task(COMPLETED, commit_hash=…) to finish the loop.",
              },
            ].map(({ step, title, body }) => (
              <div key={step} className="flex flex-col gap-3">
                <span className="font-mono text-4xl font-bold text-primary/20">
                  {step}
                </span>
                <h3 className="text-base font-semibold">{title}</h3>
                <p className="text-sm leading-6 text-muted-foreground">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="rounded-2xl border border-border bg-card p-8 sm:p-12">
          <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">
                Start free. Scale when you need to.
              </h2>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              <Button asChild>
                <Link to="/sign-up">
                  Start free
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="group rounded-2xl border border-border bg-card p-6 transition-shadow hover:shadow-md">
      <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
        {icon}
      </div>
      <h3 className="mb-2 font-semibold">{title}</h3>
      <p className="text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}
